// src/pages/duplicateSentData/DuplicateSentDataUI.jsx

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import SendToCustomerModal from './SendToCustomerModal';
import {format} from 'date-fns';
import {formatDateToJerusalem} from '../../utils/dateFormatter';
import EditDuplicateModal from './EditDuplicateModal';
import MultiCustomerDuplicates from './MultiCustomerDuplicates';
import ApiDuplicates from './ApiDuplicates';
import LeadFailureHistoryModal from '../../components/LeadFailureHistoryModal';

const DuplicateSentDataUI = ({
  // Data
  dataToShow,
  totalCount,
  gettingData,

  // Pagination
  pageNumber,
  pageSize,

  // Filters
  filters,
  handleFilterChange,
  handleClearFilters,

  // Actions
  onViewCustomers,
  onViewLead,
  onSendToCustomer,
  onSendToCustomerEnhanced,
  onFetchCustomersListByDomain,
  onFetchCustomersForModal,
  onValidateDuplicate,
  onTransferToFreeData,
  onDeleteDuplicate,
  onRefresh,
  onUpdateDuplicate,

  // Enhanced Send Functionality
  sendingLoading,
  sendingResults,
  domains,
  customers,
  emailSentHistory,

  // SSE Progress tracking props
  allStatusCount,
  doneStatusCount,
  errorStatusCount,
  errorData,

  // Modals
  showEditModal,
  showCustomerModal,
  showSendModal,
  editingDuplicate,
  viewingCustomers,
  onCloseEditModal,
  onCloseCustomerModal,
  onCloseSendModal,
  onCloseLeadModal,

  // New tab data
  multiCustomerData,
  multiCustomerLoading,
  multiCustomerPagination,
  activeTab,
  onTabChange,
  onMultiCustomerSearch,
  onRefreshMulti,
  onMultiCustomerPageChange,
  onMultiCustomerPageSizeChange,
  // Lead modal
  showLeadModal,
  viewingLead,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmTransfer, setConfirmTransfer] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLeadId, setHistoryLeadId] = useState(null);
  const [historyIsDuplicate, setHistoryIsDuplicate] = useState(false);

  // Handle transfer with validation and confirmation
  const handleTransferConfirm = async () => {
    if (!confirmTransfer) return;

    const duplicateId = confirmTransfer.id;

    try {
      // First validate the data
      const validationResult = await onValidateDuplicate(duplicateId, {
        email: confirmTransfer.duplicate_data.email,
        phone: confirmTransfer.duplicate_data.phone,
        MPC_4: confirmTransfer.duplicate_data.MPC_4,
      });

      if (!validationResult) {
        return;
      }

      // If validation passes, proceed with transfer
      await onTransferToFreeData(duplicateId);
    } catch (error) {
      console.error('Transfer failed:', error);
    } finally {
      setConfirmTransfer(null);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (duplicate) => {
    setConfirmDelete(duplicate);
  };

  const handleDeleteConfirmAction = () => {
    if (confirmDelete) {
      onDeleteDuplicate(confirmDelete.id);
      setConfirmDelete(null);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={3}
      >
        <Typography variant='h4' component='h1'>
          Duplicate Data Management
        </Typography>
        <Box>
          <Button
            variant='outlined'
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{mr: 1}}
          >
            Filters
          </Button>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 3}}>
        <Tabs
          value={activeTab || 0}
          onChange={onTabChange}
          aria-label='duplicate data tabs'
        >
          <Tab label='Sent to Multiple Customers' />
          <Tab label='Duplicates From API' />
        </Tabs>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{mb: 3}}>
          <CardContent>
            <Typography variant='h6' gutterBottom>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Search'
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder='Search by name, email, phone, marks, MPC_4, or other data content'
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Country'
                  value={filters.country}
                  onChange={(e) =>
                    handleFilterChange('country', e.target.value)
                  }
                  placeholder='Filter by country'
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Data Source'
                  value={filters.dataSource}
                  onChange={(e) =>
                    handleFilterChange('dataSource', e.target.value)
                  }
                  placeholder='Filter by data source'
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label='Integration Source'
                  value={filters.integrationSource}
                  onChange={(e) =>
                    handleFilterChange('integrationSource', e.target.value)
                  }
                  placeholder='Enter integration source'
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Domain</InputLabel>
                  <Select
                    value={filters.domain || ''}
                    label='Domain'
                    onChange={(e) => {
                      const val = e.target.value;
                      handleFilterChange('domain', val);
                      // Fetch customers for selected domain (or all when cleared)
                      if (onFetchCustomersListByDomain) {
                        onFetchCustomersListByDomain(val || null);
                      }
                      // Clear selected customer when domain changes
                      handleFilterChange('customer', '');
                    }}
                  >
                    <MenuItem value=''>All Domains</MenuItem>
                    {domains.map((d) => (
                      <MenuItem key={d.key || d.id} value={d.key || d.id}>
                        {d.label || d.domain_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={filters.customer || ''}
                    label='Customer'
                    onChange={(e) =>
                      handleFilterChange('customer', e.target.value)
                    }
                  >
                    <MenuItem value=''>All Customers</MenuItem>
                    {customers.map((c) => (
                      <MenuItem key={c.key || c.id} value={c.key || c.id}>
                        {c.label || c.user_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Created Date Range */}
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom>
                  Created date
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type='date'
                  label='Date From'
                  value={
                    Array.isArray(filters.createdDateRange) &&
                    filters.createdDateRange[0]
                      ? format(
                          new Date(filters.createdDateRange[0]),
                          'yyyy-MM-dd',
                        )
                      : ''
                  }
                  onChange={(e) => {
                    const next = Array.isArray(filters.createdDateRange)
                      ? [...filters.createdDateRange]
                      : [null, null];
                    next[0] = e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null;
                    handleFilterChange('createdDateRange', next);
                  }}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type='date'
                  label='Date To'
                  value={
                    Array.isArray(filters.createdDateRange) &&
                    filters.createdDateRange[1]
                      ? format(
                          new Date(filters.createdDateRange[1]),
                          'yyyy-MM-dd',
                        )
                      : ''
                  }
                  onChange={(e) => {
                    const next = Array.isArray(filters.createdDateRange)
                      ? [...filters.createdDateRange]
                      : [null, null];
                    next[1] = e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null;
                    handleFilterChange('createdDateRange', next);
                  }}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              {/* Sent Date Range */}
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom>
                  Sent date
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type='date'
                  label='Date From'
                  value={
                    Array.isArray(filters.sentDateRange) &&
                    filters.sentDateRange[0]
                      ? format(new Date(filters.sentDateRange[0]), 'yyyy-MM-dd')
                      : ''
                  }
                  onChange={(e) => {
                    const next = Array.isArray(filters.sentDateRange)
                      ? [...filters.sentDateRange]
                      : [null, null];
                    next[0] = e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null;
                    handleFilterChange('sentDateRange', next);
                  }}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type='date'
                  label='Date To'
                  value={
                    Array.isArray(filters.sentDateRange) &&
                    filters.sentDateRange[1]
                      ? format(new Date(filters.sentDateRange[1]), 'yyyy-MM-dd')
                      : ''
                  }
                  onChange={(e) => {
                    const next = Array.isArray(filters.sentDateRange)
                      ? [...filters.sentDateRange]
                      : [null, null];
                    next[1] = e.target.value
                      ? new Date(e.target.value).toISOString()
                      : null;
                    handleFilterChange('sentDateRange', next);
                  }}
                  InputLabelProps={{shrink: true}}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant='outlined'
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tab Content */}
      {activeTab === 0 && (
        <MultiCustomerDuplicates
          data={multiCustomerData}
          loading={multiCustomerLoading}
          pagination={multiCustomerPagination}
          onSearch={onMultiCustomerSearch}
          onView={(customers) => onViewCustomers(customers)}
          onViewLead={onViewLead}
          onSend={(row) => {
            // Transform multi-customer data to match send modal expected format
            const transformedData = {
              id: row.id,
              duplicate_data: {
                fullName: row.fullName,
                email: row.email,
                phone: row.phone,
                MPC_4: row.MPC_4 || '',
                country: row.country,
              },
              status: 'pending', // Default status for multi-customer data
            };
            console.log('Multi-customer send data transformation:', {
              original: row,
              transformed: transformedData,
            });
            onSendToCustomer(transformedData);
          }}
          onRefresh={onRefreshMulti}
          onPageChange={onMultiCustomerPageChange}
          onPageSizeChange={onMultiCustomerPageSizeChange}
          onOpenLeadHistory={(row) => {
            setHistoryLeadId(row?.id);
            setHistoryIsDuplicate(false);
            setHistoryModalOpen(true);
          }}
        />
      )}

      {/* Duplicates From API Tab */}
      {activeTab === 1 && (
        <ApiDuplicates
          data={dataToShow}
          loading={gettingData}
          pagination={{
            page: pageNumber + 1,
            total: totalCount,
            totalPages: Math.ceil(totalCount / pageSize),
          }}
          onSearch={(searchTerm) => {
            handleFilterChange('search', searchTerm);
          }}
          onRefresh={onRefresh}
          onView={(duplicate) => onViewCustomers(duplicate.customers_sent_to)}
          onViewLead={onViewLead}
          onDelete={handleDeleteConfirm}
          onSend={onSendToCustomer}
          onOpenLeadHistory={(duplicate) => {
            setHistoryLeadId(duplicate?.id);
            setHistoryIsDuplicate(true);
            setHistoryModalOpen(true);
          }}
        />
      )}

      {/* API vs System Overlap Tab removed */}

      {/* Edit Modal */}
      <EditDuplicateModal
        open={showEditModal}
        onClose={onCloseEditModal}
        duplicate={editingDuplicate}
        onUpdate={onUpdateDuplicate}
        onValidate={onValidateDuplicate}
      />

      {/* Customer Details Modal */}
      <Dialog
        open={showCustomerModal}
        onClose={onCloseCustomerModal}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Customers Sent To</DialogTitle>
        <DialogContent>
          <List>
            {viewingCustomers.map((customer, index) => (
              <React.Fragment key={index}>
                <ListItem>
                  <ListItemText
                    primary={
                      customer.user_name ||
                      customer.customer_name ||
                      'Unknown Customer'
                    }
                    secondary={`Sent at: ${formatDateToJerusalem(
                      customer.sent_at,
                    )}`}
                  />
                </ListItem>
                {index < viewingCustomers.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseCustomerModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Send to Customer Modal */}
      <SendToCustomerModal
        open={showSendModal}
        onClose={onCloseSendModal}
        duplicateData={editingDuplicate}
        onSend={onSendToCustomerEnhanced}
        onFetchCustomersByDomain={onFetchCustomersForModal}
        sendingLoading={sendingLoading}
        sendingResults={sendingResults}
        domains={domains}
        customers={customers}
        emailSentHistory={emailSentHistory}
        allStatusCount={allStatusCount}
        doneStatusCount={doneStatusCount}
        errorStatusCount={errorStatusCount}
        errorData={errorData}
      />

      {/* Lead Details Modal */}
      <Dialog
        open={!!showLeadModal}
        onClose={onCloseLeadModal}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Lead Details</DialogTitle>
        <DialogContent>
          {viewingLead ? (
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant='h6' gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  ID
                </Typography>
                <Typography variant='body2' sx={{fontWeight: 'bold'}}>
                  {viewingLead.id}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Full Name
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.fullName || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Email
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.email || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Phone
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.phone || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Age
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.age || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Country
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.country || 'N/A'}
                </Typography>
              </Grid>

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant='h6' gutterBottom sx={{mt: 2}}>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant='caption' color='text.secondary'>
                  Marks/Description
                </Typography>
                <Typography variant='body2' sx={{wordBreak: 'break-word'}}>
                  {viewingLead.marks || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  MPC_4/Funnel
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.MPC_4 || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Form ID
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.formId || 'N/A'}
                </Typography>
              </Grid>

              {/* Source Information */}
              <Grid item xs={12}>
                <Typography variant='h6' gutterBottom sx={{mt: 2}}>
                  Source Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Data Source
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.data_source || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Integration Source
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.integration_source || 'N/A'}
                </Typography>
              </Grid>

              {/* Timestamps */}
              <Grid item xs={12}>
                <Typography variant='h6' gutterBottom sx={{mt: 2}}>
                  Timestamps
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Created At
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.created_at
                    ? formatDateToJerusalem(viewingLead.created_at)
                    : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Updated At
                </Typography>
                <Typography variant='body2'>
                  {viewingLead.updated_at
                    ? formatDateToJerusalem(viewingLead.updated_at)
                    : 'N/A'}
                </Typography>
              </Grid>

              {/* Other Data */}
              {viewingLead.other_data && (
                <Grid item xs={12}>
                  <Typography variant='h6' gutterBottom sx={{mt: 2}}>
                    Other Data
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    Additional Information
                  </Typography>
                  <Typography variant='body2' sx={{wordBreak: 'break-word'}}>
                    {typeof viewingLead.other_data === 'object'
                      ? JSON.stringify(viewingLead.other_data, null, 2)
                      : viewingLead.other_data}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography variant='body2'>No lead data available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseLeadModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lead Failure History Modal */}
      <LeadFailureHistoryModal
        open={historyModalOpen}
        onClose={() => {
          setHistoryModalOpen(false);
          setHistoryLeadId(null);
          setHistoryIsDuplicate(false);
        }}
        leadId={historyLeadId}
        isDuplicate={historyIsDuplicate}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this duplicate data? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirmAction}
            color='error'
            variant='contained'
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Confirmation Dialog */}
      <Dialog
        open={!!confirmTransfer}
        onClose={() => setConfirmTransfer(null)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Confirm Transfer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to transfer this duplicate data to free data?
            This will make it available for new customer assignments.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTransfer(null)}>Cancel</Button>
          <Button
            onClick={handleTransferConfirm}
            color='primary'
            variant='contained'
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

DuplicateSentDataUI.propTypes = {
  // Data
  dataToShow: PropTypes.array.isRequired,
  totalCount: PropTypes.number.isRequired,
  gettingData: PropTypes.bool.isRequired,

  // Pagination
  pageNumber: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,

  // Filters
  filters: PropTypes.object.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  handleClearFilters: PropTypes.func.isRequired,

  // Actions
  onViewCustomers: PropTypes.func.isRequired,
  onViewLead: PropTypes.func.isRequired,
  onSendToCustomer: PropTypes.func.isRequired,
  onSendToCustomerEnhanced: PropTypes.func.isRequired,
  onFetchCustomersListByDomain: PropTypes.func,
  onFetchCustomersForModal: PropTypes.func,
  onValidateDuplicate: PropTypes.func.isRequired,
  onTransferToFreeData: PropTypes.func.isRequired,
  onDeleteDuplicate: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onUpdateDuplicate: PropTypes.func.isRequired,

  // Enhanced Send Functionality
  sendingLoading: PropTypes.bool.isRequired,
  sendingResults: PropTypes.object,
  domains: PropTypes.array.isRequired,
  customers: PropTypes.array.isRequired,
  emailSentHistory: PropTypes.array.isRequired,

  // SSE Progress tracking props
  allStatusCount: PropTypes.number.isRequired,
  doneStatusCount: PropTypes.number.isRequired,
  errorStatusCount: PropTypes.number.isRequired,
  errorData: PropTypes.array.isRequired,

  // Modals
  showEditModal: PropTypes.bool.isRequired,
  showCustomerModal: PropTypes.bool.isRequired,
  showSendModal: PropTypes.bool.isRequired,
  editingDuplicate: PropTypes.object,
  viewingCustomers: PropTypes.array.isRequired,
  onCloseEditModal: PropTypes.func.isRequired,
  onCloseCustomerModal: PropTypes.func.isRequired,
  onCloseSendModal: PropTypes.func.isRequired,
  onCloseLeadModal: PropTypes.func.isRequired,

  // New tab data
  multiCustomerData: PropTypes.array.isRequired,
  multiCustomerLoading: PropTypes.bool.isRequired,
  multiCustomerPagination: PropTypes.object.isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onMultiCustomerSearch: PropTypes.func.isRequired,
  onRefreshMulti: PropTypes.func,
  onMultiCustomerPageChange: PropTypes.func.isRequired,
  onMultiCustomerPageSizeChange: PropTypes.func.isRequired,
  // Lead modal
  showLeadModal: PropTypes.bool,
  viewingLead: PropTypes.object,
};

export default DuplicateSentDataUI;
